import useCustomerLocations, {
  CustomerLocationDTO,
} from 'src/api/accounts-receivable/customers/useCustomerLocations';
import { T, Var, useGT } from 'gt-react';
import { Autocomplete } from '@/components/common/inputs/autocomplete';
import { pickerLabels } from '@/components/common/inputs/pickerLabels';
import { ErrorIcon } from '@/components/common/semantic-icons';
import { cn } from '@/lib/utils';
import LocationPill from 'src/components/location/LocationPill';

/**
 * EDI_PARTY_NOT_SHOWN_NOTE
 *
 * The EDI bill-to and buying-party locations are **not shown** on sales order
 * drafts — no picker, no read-only row. Only ship-to is.
 *
 * They drive nothing on the Famous path: the FAPI `H1` record carries only the
 * customer DUNS and the ship-to location, and the outbound 810 / 855 / 856 / 880
 * echo `N1*BT` / `N1*BY` straight from the stored raw 850
 * (`resolve810Parties*.ts`), never from these FKs. Showing them as editable
 * fields did active harm: when a filtered option list hid the matched location
 * the field read as blank, so ops "fixed" it by selecting the ship-to DC — 86
 * such overwrites on United Supermarkets drafts in 45 days.
 *
 * Nothing is lost by hiding them. `EdiTransactionViewer`'s "Party Information"
 * section already lists every `N1` loop the partner sent — entity code, name,
 * identification code and address — so the parties remain inspectable on the
 * draft for anyone diagnosing a match, which is the only reason to look.
 *
 * The matcher still resolves them (`matchEdiStagedPOLocations`, identifier-only,
 * fails closed) and they are still persisted staged-PO → SalesOrder. Keep it
 * that way: `billToLocationId` IS functional on the OMS v3 registered-API
 * submission path (`buildSubmissionScopes` exposes `billToLocation.originId` to
 * the submission map), which uses a different draft model and never rendered
 * these fields.
 */

function LocationItem({
  location,
  isTrigger = false,
}: {
  location: CustomerLocationDTO;
  isTrigger?: boolean;
}) {
  return (
    <div className="flex items-center gap-1">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">
          {location.name || (
            <T context="fallback when customer location has no name">
              Unnamed Location
            </T>
          )}
        </span>
        {!isTrigger && (
          <span className="text-xs text-muted-foreground">
            {location.address || (
              <T context="fallback when location has no address">
                No address provided
              </T>
            )}
          </span>
        )}
        {!isTrigger && location.dunsNumber && (
          <span className="text-xs text-muted-foreground">
            <T context="DUNS identifier label with number">
              DUNS: <Var>{location.dunsNumber}</Var>
            </T>
          </span>
        )}
      </div>
    </div>
  );
}

export function LocationPicker({
  onLocationSelect,
  selectedLocationId,
  customerId,
  className,
  error,
  placeholder = 'Select a location',
  emptyMessage = 'No locations found for this customer',
  canShipTo,
  includeDisabled = false,
}: {
  onLocationSelect: (locationId?: string | undefined) => void;
  selectedLocationId?: string;
  customerId: string;
  className?: string;
  error?: string;
  placeholder?: string;
  emptyMessage?: string;
  /**
   * Filter on Famous's `ShipToLocationFlag`. `true` lists the customer's DCs;
   * `false` lists the non-ship-to locations — the accounting parties named as
   * bill-to / buying party on an EDI order. Omitted lists both.
   */
  canShipTo?: boolean;
  /**
   * Include locations flagged `disabled` in Famous.
   *
   * The Famous customer ingest disables every non-ship-to location
   * (`ShipToLocationFlag = 'N'`), which is precisely what a customer's bill-to
   * and buying-party locations are. With the default active-only list those
   * locations are absent from `options`, so a correctly matched selection has
   * no matching option and the picker falls back to its placeholder — reading
   * as "nothing is assigned" when in fact the EDI matcher resolved it. Set for
   * the bill-to / buying-party pickers, mirroring `includeDisabled` on the
   * backend's `EDI_LOCATION_ROLES`.
   */
  includeDisabled?: boolean;
}) {
  const gt = useGT();

  const {
    data: locationsData,
    isLoading: isLoadingLocations,
    error: locationsLoadError,
  } = useCustomerLocations(customerId, !!customerId, {
    activeOnly: !includeDisabled,
    canShipTo,
    // The selected location is always listed, whatever the filters say. Without
    // this the Autocomplete finds no option for `value` and falls back to its
    // placeholder, so an assigned location reads as "nothing assigned" — and
    // ops overwrites a correct match. See `buildCustomerLocationsWhere`.
    includeLocationId: selectedLocationId,
  });

  const locations: CustomerLocationDTO[] = locationsData?.locations || [];

  // Custom filter function to handle null values safely
  const filterFunction = (location: CustomerLocationDTO, search: string) => {
    if (!search) return true;

    const searchLower = search.toLowerCase();
    const name = location.name || '';
    const address = location.address || '';
    const id = location.id || '';

    return (
      name.toLowerCase().includes(searchLower) ||
      address.toLowerCase().includes(searchLower) ||
      id.toLowerCase().includes(searchLower)
    );
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className={cn(error && 'ring-2 ring-red-500 rounded-md p-1')}>
        <Autocomplete<CustomerLocationDTO>
          items={locations}
          value={selectedLocationId || null}
          disabled={isLoadingLocations}
          labels={pickerLabels({
            placeholder,
            search: gt('Search options...', {
              context: 'search box placeholder inside a picker dropdown',
            }),
          })}
          getValue={(location: CustomerLocationDTO) => location.id || ''}
          getLabel={(location: CustomerLocationDTO) =>
            location.name ||
            gt('Unnamed Location', {
              context: 'fallback label when customer location has no name',
            })
          }
          renderItem={(location: CustomerLocationDTO) => (
            <LocationItem location={location} />
          )}
          indicator="none"
          renderValue={({ selected }) => (
            <LocationPill
              location={{
                name: selected[0].name,
                address: selected[0].address,
              }}
            />
          )}
          onValueChange={(next) => onLocationSelect(next ?? '')}
          deselectable
          filter={filterFunction}
          // Without a modal popup, wheel/scroll events are blocked when this
          // picker renders inside a modal Dialog (e.g. the sales order draft
          // modal) — same as EditLineItemModal's autocomplete.
          modal
        />
      </div>
      {locationsLoadError && (
        <div className="flex items-center gap-1">
          <ErrorIcon />
          <span className="text-sm text-muted-foreground">
            {gt('Failed to load customer locations: {message}', {
              context: 'error when customer locations API fails',
              message: locationsLoadError.message,
            })}
          </span>
        </div>
      )}
      {!isLoadingLocations && locations.length === 0 && (
        <div className="flex items-center gap-1">
          <span className="text-sm text-muted-foreground">{emptyMessage}</span>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-1">
          <ErrorIcon />
          <span className="text-sm text-red-500">{error}</span>
        </div>
      )}
    </div>
  );
}
